import { FC, useContext, FormEvent, SetStateAction, Dispatch } from "react";
import {
  BundleFormModalProvider,
  BundleFormModalContext,
} from "./BundleFormModalContext";
import {
  useCreateModelBundleMutation,
  useUpdateModelBundleMutation,
} from "../../../generated/graphql";
import { Dialog, Typography } from "@material-ui/core";
import { ButtonCustom, CancelButton } from "../../shared/Style/Style";
import { BundleForm } from "../styles";
import { CloseIconButton } from "../../shared/Buttons/CloseIconButton/CloseIconButton";
import { TextareaResize } from "../../shared/TextareaResize/TextareaResize";
import { SelectModelType } from "../components/SelectModelType";
import { SelectBundleTags } from "../components/SelectBundleTags";
import { PlatformTabs } from "../components/PlatformTabs";
import { AppModelBundles } from "../components/ModelBundles";
import { DraftToggle } from "../components/DraftToggle";
import { validateModelBundle } from "../utils";
import { DialogHeader } from "../../MobileAppModal/MobileAppModal.style";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress";
import { initialNotifications } from "../BundlesStorage";

interface BundleFormModalProps {
  id?: string;
  onClose: () => void;
  setNotifications: Dispatch<
    SetStateAction<{
      error: boolean;
      success: boolean;
      message: string;
    }>
  >;
}

const BundleFormModalContent: FC<BundleFormModalProps> = ({
  id,
  onClose,
  setNotifications,
}) => {
  const { modelData, errors, updateModelData, handleError } = useContext(
    BundleFormModalContext,
  );

  const [createModelBundle, { loading: createModelLoading }] =
    useCreateModelBundleMutation();
  const [updateModelBundle, { loading: updateModelLoading }] =
    useUpdateModelBundleMutation();

  if (id && !modelData.id) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const data = {
      isDraft: modelData.isDraft,
      name: modelData.name?.trim() || undefined,
      number: modelData.number?.trim(),
      platforms: modelData.platforms?.length ? modelData.platforms : undefined,
      tagIds: modelData.tags?.map((tag) => tag.id),
    };
    try {
      if (modelData.id) {
        await updateModelBundle({
          variables: {
            data: { ...data, id: modelData.id },
          },
          refetchQueries: ["getModelBundlesPaginatedList"],
          awaitRefetchQueries: true,
          onCompleted(data) {
            if (data?.updateModelBundle?.id) {
              setNotifications({
                ...initialNotifications,
                success: true,
                message: "Модель експерименту успішно відредагована",
              });
            }
          },
          onError: (error) => {
            setNotifications({
              ...initialNotifications,
              error: true,
              message: "Сталась помилка при редагуванні моделі експерименту",
            });
            console.error(error);
          },
        });
      } else {
        await createModelBundle({
          variables: {
            data: { ...data, contentType: modelData.contentType },
          },
          refetchQueries: ["getModelBundlesPaginatedList"],
          awaitRefetchQueries: true,
          onCompleted(data) {
            if (data?.createModelBundle?.id) {
              setNotifications({
                ...initialNotifications,
                success: true,
                message: "Модель експерименту успішно створена",
              });
            }
          },
          onError: (error) => {
            setNotifications({
              ...initialNotifications,
              error: true,
              message: "Сталась помилка при створенні моделі експерименту",
            });
            console.error(error);
          },
        });
      }
    } catch {
      console.error("Error while saving model bundle");
      setNotifications({
        ...initialNotifications,
        error: true,
        message: "Сталась помилка, спробуйте пізніше",
      });
    } finally {
      onClose();
    }
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          padding: "32px",
        },
      }}
    >
      {(createModelLoading || updateModelLoading) && <LoadingProgressOverlay />}
      <DialogHeader>
        <Typography variant="h6">{`${
          modelData.id ? "Редагування" : "Створення"
        } моделі`}</Typography>
        <CloseIconButton close={onClose} />
      </DialogHeader>

      <BundleForm onSubmit={handleSubmit}>
        <TextareaResize
          value={modelData.name}
          error={errors?.name}
          label="Назва *"
          style={{ width: "100%" }}
          maxLength={100}
          onFocus={() => handleError("name", false)}
          onChange={({ target: { value } }) => updateModelData("name", value)}
        />

        <TextareaResize
          value={modelData.number}
          error={errors?.number}
          label="Номер"
          style={{ width: "100%" }}
          maxLength={10}
          onFocus={() => handleError("number", false)}
          onChange={({ target: { value } }) => updateModelData("number", value)}
        />
        <SelectModelType />
        <SelectBundleTags />
        <PlatformTabs />
        <AppModelBundles setNotifications={setNotifications} />
        <DraftToggle />
        <Typography variant="body2" color="textSecondary">
          * - Для збереження: заповніть усі поля та додайте бандл і маніфест для
          платформи
        </Typography>
        <div style={{ display: "flex", justifyContent: "space-evenly" }}>
          <CancelButton onClick={onClose} type="button">
            Закрити
          </CancelButton>

          <ButtonCustom
            disabled={!validateModelBundle(modelData)}
            type="submit"
          >
            Зберегти
          </ButtonCustom>
        </div>
      </BundleForm>
    </Dialog>
  );
};

export const BundleFormModal: FC<BundleFormModalProps> = ({
  id,
  onClose,
  setNotifications,
}) => (
  <BundleFormModalProvider id={id}>
    <BundleFormModalContent
      id={id}
      onClose={onClose}
      setNotifications={setNotifications}
    />
  </BundleFormModalProvider>
);
